import React from 'react'
import { Image } from '@educator/components/Image'

export interface InfoBoxProps {
  title?: string
  description?: string
  /** Rich body rendered in place of `description`, outside the `<p>` so block markup nests validly. */
  children?: React.ReactNode
  variant?: 'info' | 'success' | 'error' | 'notice' | 'closed'
}

// Map a BFF notice severity to the InfoBox visual variant. Driven off severity
// rather than the screen so the accepted and checkout surfaces stay in sync:
// grace (`error`) → red, scholarship (`success`) → green, everything else
// (regular `info`, or a missing severity) → the yellow `notice` style.
const SEVERITY_TO_VARIANT: Record<string, InfoBoxProps['variant']> = {
  error: 'error',
  success: 'success',
}

export const infoBoxVariantForSeverity = (
  severity?: string,
): NonNullable<InfoBoxProps['variant']> =>
  SEVERITY_TO_VARIANT[severity ?? ''] ?? 'notice'

const INFO_ICON_URL = 'https://static.masterclass.com/jar-info.svg'
const SUCCESS_ICON_URL =
  'https://static.masterclass.com/jar-success-check-toast.svg'

const variantStyles = {
  info: {
    background: 'bg-surface-gray-2',
    textPrimary: 'text-text-primary',
    textSecondary: 'text-text-secondary',
  },
  success: {
    background: 'bg-surface-success-1',
    textPrimary: 'text-text-success',
    textSecondary: 'text-text-success',
  },
  error: {
    background: 'bg-surface-error-1',
    textPrimary: 'text-black',
    textSecondary: 'text-black',
  },
  notice: {
    background: 'bg-edu-accent-yellow',
    textPrimary: 'text-black',
    textSecondary: 'text-black',
  },
  closed: {
    // Raw hex: design's #ffa2a2 has no matching app token (--red-100 is lighter).
    background: 'bg-[#ffa2a2]',
    textPrimary: 'text-black',
    textSecondary: 'text-black',
  },
}

// ARIA role per variant: error announces assertively (implicit via role="alert",
// matching Toast), success is a polite status. Other variants are silent.
const ROLE_BY_VARIANT: Partial<
  Record<NonNullable<InfoBoxProps['variant']>, 'status' | 'alert'>
> = {
  success: 'status',
  error: 'alert',
}

export const InfoBox = ({
  title,
  description,
  children,
  variant = 'info',
}: InfoBoxProps) => {
  const styles = variantStyles[variant]
  const role = ROLE_BY_VARIANT[variant]
  const ariaLive = variant === 'success' ? 'polite' : undefined
  const iconUrl = variant === 'success' ? SUCCESS_ICON_URL : INFO_ICON_URL

  return (
    <div
      role={role}
      aria-live={ariaLive}
      data-variant={variant}
      className={`${styles.background} p-4 flex gap-3`}
    >
      <div className='flex-shrink-0'>
        <Image
          src={iconUrl}
          alt=''
          width={24}
          height={24}
          className='w-6 h-6'
        />
      </div>
      <div className='flex flex-col gap-2 text-left'>
        {title && (
          <h2
            className={`text-sm font-semibold leading-[1.2] ${styles.textPrimary} m-0 font-sans`}
          >
            {title}
          </h2>
        )}
        {children ?? (
          <p
            className={`text-sm font-normal leading-[1.2] ${styles.textSecondary} m-0 font-sans`}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  )
}
